import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Phone, Mail, MapPin, Loader2, Check, FileDown } from "lucide-react";
import { fileToUrl } from "@/content/ContentContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const empty = { name: "", company_name: "", phone: "", email: "", current_city: "" };

export default function RegisterContact({ content, packageUrl }) {
  const c = content || {};
  const [form, setForm] = useState(empty);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const update = (k) => (e) => setForm((s) => ({ ...s, [k]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await axios.post(`${API}/register`, form);
      setDone(true);
      setForm(empty);
      toast.success("Thank you — we'll be in touch shortly.");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(
        typeof detail === "string"
          ? detail
          : "Something went wrong. Please try again or call 519-377-0087."
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      id="register"
      data-testid="register-section"
      className="relative py-24 md:py-36 lg:py-44 bg-[var(--bone-2)]"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20">
        <div className="lg:col-span-5">
          <p className="overline mb-6">{c.overline}</p>
          <h2
            data-testid="register-title"
            className="font-serif-display text-3xl md:text-4xl lg:text-5xl leading-[1.15] tracking-tight font-medium"
          >
            {c.title}
          </h2>
          <p className="mt-6 text-[var(--ink-soft)] text-base md:text-lg leading-relaxed max-w-md">
            {c.body}
          </p>

          {packageUrl && (
            <a
              data-testid="leasing-package-download"
              href={fileToUrl(packageUrl)}
              target="_blank"
              rel="noreferrer"
              className="btn-secondary mt-8 inline-flex"
            >
              <FileDown size={14} /> Download Leasing Package
            </a>
          )}
        </div>

        <div className="lg:col-span-7 bg-[var(--bone)] p-8 md:p-12 lg:p-14">
          {done ? (
            <div
              data-testid="register-success"
              className="min-h-[420px] flex flex-col items-start justify-center"
            >
              <div className="w-14 h-14 rounded-full bg-[var(--sage)]/15 flex items-center justify-center mb-6">
                <Check className="text-[var(--sage)]" size={26} />
              </div>
              <p className="overline mb-3">Thank You</p>
              <h3 className="font-serif-display text-3xl md:text-4xl">
                Your interest has been received.
              </h3>
              <p className="mt-4 text-[var(--ink-soft)] max-w-md">
                Our leasing team will reach out within one business day. We've
                also emailed your leasing package, if available.
              </p>
              <button
                onClick={() => setDone(false)}
                className="btn-secondary mt-10"
                data-testid="register-submit-another"
              >
                Submit another enquiry
              </button>
            </div>
          ) : (
            <form
              data-testid="register-form"
              onSubmit={onSubmit}
              className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-7"
            >
              <div className="md:col-span-2">
                <label className="overline">Full Name *</label>
                <input
                  required
                  type="text"
                  value={form.name}
                  onChange={update("name")}
                  data-testid="register-input-name"
                  placeholder="Jane Doe"
                  className="input-line"
                />
              </div>

              <div className="md:col-span-2">
                <label className="overline">Company Name</label>
                <input
                  type="text"
                  value={form.company_name}
                  onChange={update("company_name")}
                  data-testid="register-input-company"
                  placeholder="(optional)"
                  className="input-line"
                />
              </div>

              <div>
                <label className="overline">Phone *</label>
                <input
                  required
                  type="tel"
                  value={form.phone}
                  onChange={update("phone")}
                  data-testid="register-input-phone"
                  placeholder="519-555-0100"
                  className="input-line"
                />
              </div>

              <div>
                <label className="overline">Email *</label>
                <input
                  required
                  type="email"
                  value={form.email}
                  onChange={update("email")}
                  data-testid="register-input-email"
                  placeholder="you@example.com"
                  className="input-line"
                />
              </div>

              <div className="md:col-span-2">
                <label className="overline">Current City *</label>
                <input
                  required
                  type="text"
                  value={form.current_city}
                  onChange={update("current_city")}
                  data-testid="register-input-city"
                  placeholder="Toronto, ON"
                  className="input-line"
                />
              </div>

              <div className="md:col-span-2 pt-4 flex flex-wrap items-center justify-between gap-4">
                <p className="text-xs text-[var(--ink-soft)] max-w-sm leading-relaxed">
                  {c.disclaimer}
                </p>
                <button
                  type="submit"
                  disabled={submitting}
                  data-testid="register-submit-button"
                  className="btn-primary disabled:opacity-60"
                >
                  {submitting ? (
                    <>
                      <Loader2 size={16} className="animate-spin" />
                      Sending…
                    </>
                  ) : (
                    <>Send Enquiry</>
                  )}
                </button>
              </div>
            </form>
          )}
        </div>

        <div className="lg:col-span-12 grid grid-cols-1 md:grid-cols-3 gap-8 pt-12 border-t border-[var(--line-strong)]">
          <div className="flex items-start gap-4">
            <Phone size={18} className="text-[var(--terracotta)] mt-1 shrink-0" />
            <div>
              <p className="overline">Call</p>
              <a
                href={`tel:${(content?.phone || "").replace(/[^0-9+]/g, "")}`}
                className="font-serif-display text-xl mt-1 block hover:text-[var(--terracotta)]"
                data-testid="contact-phone"
              >
                {content?.phone || ""}
              </a>
            </div>
          </div>
          <div className="flex items-start gap-4">
            <Mail size={18} className="text-[var(--terracotta)] mt-1 shrink-0" />
            <div>
              <p className="overline">Email</p>
              <a
                href={`mailto:${content?.email || ""}`}
                className="font-serif-display text-xl mt-1 block hover:text-[var(--terracotta)]"
                data-testid="contact-email"
              >
                {content?.email || ""}
              </a>
            </div>
          </div>
          <div className="flex items-start gap-4">
            <MapPin size={18} className="text-[var(--terracotta)] mt-1 shrink-0" />
            <div>
              <p className="overline">Visit</p>
              <p className="font-serif-display text-xl mt-1" data-testid="contact-address">
                {content?.address_line_1}
                <br />
                {content?.address_line_2}
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
